import { clientsAPI } from './apiClient';
import { readSnapshot, writeSnapshot, clearClientsSnapshot } from './clientsSnapshotStorage';

export { clearClientsSnapshot };

/**
 * Local snapshot + delta sync for the active-customer list.
 *
 * Strategy:
 * - First load: download all active clients (large parallel pages), persist to
 *   localStorage, and record a `watermark` (the max `updatedAt` seen).
 * - Subsequent loads/refetches: ask the backend only for rows changed since the
 *   watermark (`updatedSince`) and merge them into the snapshot. Deactivated
 *   clients are removed, new/updated ones are upserted.
 * - A full re-download happens automatically once the snapshot is older than
 *   FULL_SYNC_MAX_AGE_MS, or when forced via clearClientsSnapshot().
 */

const FULL_SYNC_MAX_AGE_MS = 24 * 60 * 60 * 1000; // force a full re-download daily
const PAGE_LIMIT = 1000;

const sortClients = (clients) =>
  [...clients].sort((a, b) => {
    const last = (a.contactLastName || '').localeCompare(b.contactLastName || '', undefined, { sensitivity: 'base' });
    if (last !== 0) return last;
    return (a.contactFirstName || '').localeCompare(b.contactFirstName || '', undefined, { sensitivity: 'base' });
  });

// updatedAt values are ISO 8601 strings from the API, so string comparison is safe.
const maxWatermark = (current, clients) =>
  clients.reduce((max, c) => (c.updatedAt && c.updatedAt > max ? c.updatedAt : max), current || '');

const fetchPage = async (params) => {
  const response = await clientsAPI.getClients(params);
  const data = response.data?.data || response.data;
  return { clients: data?.clients || [], pagination: data?.pagination };
};

// Fetches page 1 to learn the total, then the remaining pages in parallel.
const fetchAllPages = async (params, onProgress) => {
  const first = await fetchPage({ ...params, page: 1, limit: PAGE_LIMIT });
  const total = first.pagination?.total ?? first.clients.length;
  const pages = first.pagination?.pages ?? 1;
  const clients = [...first.clients];
  onProgress?.({ loaded: clients.length, total, estimatedTotal: total });

  if (pages > 1) {
    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, i) => fetchPage({ ...params, page: i + 2, limit: PAGE_LIMIT }))
    );
    rest.forEach((result) => {
      clients.push(...result.clients);
      onProgress?.({ loaded: clients.length, total, estimatedTotal: total });
    });
  }

  return clients;
};

const fullSync = async (onProgress) => {
  const clients = await fetchAllPages({ isActive: true }, onProgress);
  const snapshot = {
    clients: sortClients(clients),
    watermark: maxWatermark('', clients),
    fullSyncAt: Date.now(),
    lastSyncedAt: Date.now()
  };
  writeSnapshot(snapshot);
  return snapshot;
};

const deltaSync = async (snapshot) => {
  // No isActive filter: we need to see deactivations to remove them from the list.
  const changed = await fetchAllPages({ updatedSince: snapshot.watermark });

  if (changed.length === 0) {
    const next = { ...snapshot, lastSyncedAt: Date.now() };
    writeSnapshot(next);
    return next;
  }

  const byId = new Map(snapshot.clients.map((c) => [c.id, c]));
  changed.forEach((c) => {
    if (c.isActive === false || c.isPending === true) {
      byId.delete(c.id);
    } else {
      byId.set(c.id, c);
    }
  });

  const next = {
    clients: sortClients([...byId.values()]),
    watermark: maxWatermark(snapshot.watermark, changed),
    fullSyncAt: snapshot.fullSyncAt,
    lastSyncedAt: Date.now()
  };
  writeSnapshot(next);
  return next;
};

/**
 * Query function for react-query. Returns `{ clients }` with every active client.
 */
export const syncActiveClients = async (onProgress) => {
  const snapshot = readSnapshot();
  const isFresh = snapshot && Date.now() - snapshot.fullSyncAt < FULL_SYNC_MAX_AGE_MS;

  if (!isFresh) {
    const full = await fullSync(onProgress);
    return { clients: full.clients };
  }

  try {
    const next = await deltaSync(snapshot);
    return { clients: next.clients };
  } catch {
    const full = await fullSync(onProgress);
    return { clients: full.clients };
  }
};

/** Seed data for react-query's initialData so the page renders instantly after a reload. */
export const getClientsSnapshotData = () => {
  const snapshot = readSnapshot();
  return snapshot ? { clients: snapshot.clients } : undefined;
};

export const getClientsSnapshotUpdatedAt = () => {
  const snapshot = readSnapshot();
  return snapshot?.lastSyncedAt;
};
